import { Plus, Trash2, GripVertical, BarChart3, BarChartHorizontal, LayoutGrid, Info, TrendingUp } from 'lucide-react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Label } from '@/shared/components/ui/label'
import { Input } from '@/shared/components/ui/input'
import { Switch } from '@/shared/components/ui/switch'
import { Button } from '@/shared/components/ui/button'
import { Separator } from '@/shared/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { Checkbox } from '@/shared/components/ui/checkbox'
import type {
  CardConfig,
  CardsConfig,
  GraficaBarrasItemConfig,
  GraficasBarrasConfig,
  GraficaLineasItemConfig,
  GraficasLineasConfig,
  VisualizacionConfig,
  TipoAgregacion,
  ColumnaTabla,
} from '../types'

// Default visualization config
export const defaultVisualizacionConfig: VisualizacionConfig = {
  cards: {
    habilitado: false,
    cards: [],
  },
  graficas_verticales: {
    habilitado: false,
    graficas: [],
  },
  graficas_horizontales: {
    habilitado: false,
    graficas: [],
  },
  graficas_lineas: {
    habilitado: false,
    graficas: [],
  },
}

// Generate unique ID (with fallback for non-secure contexts)
const generateId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback for non-secure contexts
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// Color options for bar chart
const COLORES_GRAFICA = [
  { value: '#2fbc80', label: 'Verde Tonal' },
  { value: '#57a6d8', label: 'Azul Tonal' },
  { value: '#8d82bc', label: 'Morado Tonal' },
  { value: '#eb6587', label: 'Rosa Tonal' },
  { value: '#f6c23e', label: 'Amarillo' },
  { value: '#858796', label: 'Gris' },
]

// Sortable Card Item Component
interface SortableCardItemProps {
  card: CardConfig
  columnas: ColumnaTabla[]
  onUpdate: (id: string, updates: Partial<CardConfig>) => void
  onRemove: (id: string) => void
}

function SortableCardItem({ card, columnas, onUpdate, onRemove }: SortableCardItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg p-3 bg-white dark:bg-card ${isDragging ? 'opacity-50 shadow-lg' : ''}`}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded mt-1 touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>

        <div className="flex-1 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Título</Label>
              <Input
                value={card.titulo}
                onChange={(e) => onUpdate(card.id, { titulo: e.target.value })}
                placeholder="Total Registros"
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Columna</Label>
              <Select
                value={card.columna}
                onValueChange={(value) => onUpdate(card.id, { columna: value })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {columnas.map((col) => (
                    <SelectItem key={col.nombre} value={col.nombre}>
                      {col.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Tipo Agregación</Label>
              <Select
                value={card.tipo_agregacion}
                onValueChange={(value) => onUpdate(card.id, { tipo_agregacion: value as TipoAgregacion })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="COUNT">COUNT (Total)</SelectItem>
                  <SelectItem value="DISTINCT_COUNT">DISTINCT (Únicos)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Subtítulo (opcional)</Label>
              <Input
                value={card.subtitulo || ''}
                onChange={(e) => onUpdate(card.id, { subtitulo: e.target.value })}
                placeholder="Descripción breve"
                className="h-8 text-sm"
              />
            </div>
          </div>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive hover:text-destructive shrink-0"
          onClick={() => onRemove(card.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

// Sortable Grafica Item Component
interface SortableGraficaItemProps {
  grafica: GraficaBarrasItemConfig
  columnas: ColumnaTabla[]
  onUpdate: (id: string, updates: Partial<GraficaBarrasItemConfig>) => void
  onRemove: (id: string) => void
  index: number
}

function SortableGraficaItem({ grafica, columnas, onUpdate, onRemove, index }: SortableGraficaItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: grafica.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg p-3 bg-white dark:bg-card ${isDragging ? 'opacity-50 shadow-lg' : ''}`}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded mt-1 touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>

        <div className="flex-1 space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-muted-foreground">Gráfica {index + 1}</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Columna (Agrupación) *</Label>
              <Select
                value={grafica.columna_agrupacion}
                onValueChange={(value) => onUpdate(grafica.id, { columna_agrupacion: value })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Seleccionar columna" />
                </SelectTrigger>
                <SelectContent>
                  {columnas.map((col) => (
                    <SelectItem key={col.nombre} value={col.nombre}>
                      {col.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Título</Label>
              <Input
                value={grafica.titulo}
                onChange={(e) => onUpdate(grafica.id, { titulo: e.target.value })}
                placeholder="Distribución por..."
                className="h-8 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Color</Label>
              <Select
                value={grafica.color}
                onValueChange={(value) => onUpdate(grafica.id, { color: value })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: grafica.color }}
                    />
                    <span>{COLORES_GRAFICA.find((c) => c.value === grafica.color)?.label || 'Color'}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {COLORES_GRAFICA.map((color) => (
                    <SelectItem key={color.value} value={color.value}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: color.value }}
                        />
                        <span>{color.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Límite de categorías</Label>
              <Input
                type="number"
                value={grafica.limite_categorias}
                onChange={(e) =>
                  onUpdate(grafica.id, { limite_categorias: Math.min(50, Math.max(1, parseInt(e.target.value) || 10)) })
                }
                min={1}
                max={50}
                className="h-8 text-sm"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id={`mostrar_etiquetas_${grafica.id}`}
              checked={grafica.mostrar_etiquetas}
              onCheckedChange={(checked) =>
                onUpdate(grafica.id, { mostrar_etiquetas: checked as boolean })
              }
            />
            <Label htmlFor={`mostrar_etiquetas_${grafica.id}`} className="text-xs cursor-pointer">
              Mostrar valores sobre las barras
            </Label>
          </div>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive hover:text-destructive shrink-0"
          onClick={() => onRemove(grafica.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

// Cards Configuration Component
interface CardsConfigurationProps {
  config: CardsConfig
  columnas: ColumnaTabla[]
  onChange: (config: CardsConfig) => void
}

function CardsConfiguration({ config, columnas, onChange }: CardsConfigurationProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  // Ensure cards array exists
  const cards = config?.cards ?? []

  const handleToggle = (habilitado: boolean) => {
    onChange({ ...config, habilitado, cards })
  }

  const handleAddCard = () => {
    const newCard: CardConfig = {
      id: generateId(),
      titulo: '',
      columna: columnas[0]?.nombre || '',
      tipo_agregacion: 'COUNT',
      subtitulo: '',
      orden: cards.length,
    }
    onChange({
      ...config,
      cards: [...cards, newCard],
    })
  }

  const handleUpdateCard = (id: string, updates: Partial<CardConfig>) => {
    onChange({
      ...config,
      cards: cards.map((card) =>
        card.id === id ? { ...card, ...updates } : card
      ),
    })
  }

  const handleRemoveCard = (id: string) => {
    onChange({
      ...config,
      cards: cards.filter((card) => card.id !== id),
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = cards.findIndex((c) => c.id === active.id)
      const newIndex = cards.findIndex((c) => c.id === over.id)
      const newCards = arrayMove(cards, oldIndex, newIndex).map((card, i) => ({
        ...card,
        orden: i,
      }))
      onChange({ ...config, cards: newCards })
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <LayoutGrid className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-semibold text-sm">Cards de Métricas</h3>
        </div>
        <Switch checked={config?.habilitado ?? false} onCheckedChange={handleToggle} />
      </div>

      {config?.habilitado && (
        <div className="space-y-3 pl-6">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={cards.map((c) => c.id)}
              strategy={verticalListSortingStrategy}
            >
              {cards.map((card) => (
                <SortableCardItem
                  key={card.id}
                  card={card}
                  columnas={columnas}
                  onUpdate={handleUpdateCard}
                  onRemove={handleRemoveCard}
                />
              ))}
            </SortableContext>
          </DndContext>

          {cards.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No hay cards configuradas. Agrega una para mostrar métricas.
            </p>
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleAddCard}
          >
            <Plus className="h-4 w-4 mr-2" />
            Agregar Card
          </Button>
        </div>
      )}
    </div>
  )
}

// Graficas Barras Configuration Component (para múltiples gráficas)
interface GraficasBarrasConfigurationProps {
  config: GraficasBarrasConfig
  columnas: ColumnaTabla[]
  onChange: (config: GraficasBarrasConfig) => void
  tipo: 'vertical' | 'horizontal'
}

function GraficasBarrasConfiguration({ config, columnas, onChange, tipo }: GraficasBarrasConfigurationProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const graficas = config?.graficas ?? []

  const handleToggle = (habilitado: boolean) => {
    onChange({ ...config, habilitado, graficas })
  }

  const handleAddGrafica = () => {
    const newGrafica: GraficaBarrasItemConfig = {
      id: generateId(),
      columna_agrupacion: columnas[0]?.nombre || '',
      titulo: `Distribución ${graficas.length + 1}`,
      color: COLORES_GRAFICA[graficas.length % COLORES_GRAFICA.length].value,
      mostrar_etiquetas: true,
      limite_categorias: 10,
      orden: graficas.length,
    }
    onChange({
      ...config,
      graficas: [...graficas, newGrafica],
    })
  }

  const handleUpdateGrafica = (id: string, updates: Partial<GraficaBarrasItemConfig>) => {
    onChange({
      ...config,
      graficas: graficas.map((g) =>
        g.id === id ? { ...g, ...updates } : g
      ),
    })
  }

  const handleRemoveGrafica = (id: string) => {
    onChange({
      ...config,
      graficas: graficas.filter((g) => g.id !== id),
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = graficas.findIndex((g) => g.id === active.id)
      const newIndex = graficas.findIndex((g) => g.id === over.id)
      const newGraficas = arrayMove(graficas, oldIndex, newIndex).map((g, i) => ({
        ...g,
        orden: i,
      }))
      onChange({ ...config, graficas: newGraficas })
    }
  }

  const Icon = tipo === 'vertical' ? BarChart3 : BarChartHorizontal
  const titulo = tipo === 'vertical' ? 'Gráficas de Barras Verticales' : 'Gráficas de Barras Horizontales'

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-semibold text-sm">{titulo}</h3>
        </div>
        <Switch checked={config?.habilitado ?? false} onCheckedChange={handleToggle} />
      </div>

      {config?.habilitado && (
        <div className="space-y-3 pl-6">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={graficas.map((g) => g.id)}
              strategy={verticalListSortingStrategy}
            >
              {graficas.map((grafica, index) => (
                <SortableGraficaItem
                  key={grafica.id}
                  grafica={grafica}
                  columnas={columnas}
                  onUpdate={handleUpdateGrafica}
                  onRemove={handleRemoveGrafica}
                  index={index}
                />
              ))}
            </SortableContext>
          </DndContext>

          {graficas.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No hay gráficas configuradas. Agrega una para visualizar distribuciones.
            </p>
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleAddGrafica}
          >
            <Plus className="h-4 w-4 mr-2" />
            Agregar Gráfica {tipo === 'vertical' ? 'Vertical' : 'Horizontal'}
          </Button>
        </div>
      )}
    </div>
  )
}

// Sortable Grafica Linea Item Component
interface SortableGraficaLineaItemProps {
  grafica: GraficaLineasItemConfig
  columnas: ColumnaTabla[]
  onUpdate: (id: string, updates: Partial<GraficaLineasItemConfig>) => void
  onRemove: (id: string) => void
  index: number
}

function SortableGraficaLineaItem({ grafica, columnas, onUpdate, onRemove, index }: SortableGraficaLineaItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: grafica.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg p-3 bg-white dark:bg-card ${isDragging ? 'opacity-50 shadow-lg' : ''}`}
    >
      <div className="flex items-start gap-2">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded mt-1 touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>

        <div className="flex-1 space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-muted-foreground">Gráfica {index + 1}</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Columna (Agrupación) *</Label>
              <Select
                value={grafica.columna_agrupacion}
                onValueChange={(value) => onUpdate(grafica.id, { columna_agrupacion: value })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Seleccionar columna" />
                </SelectTrigger>
                <SelectContent>
                  {columnas.map((col) => (
                    <SelectItem key={col.nombre} value={col.nombre}>
                      {col.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Título</Label>
              <Input
                value={grafica.titulo}
                onChange={(e) => onUpdate(grafica.id, { titulo: e.target.value })}
                placeholder="Tendencia por..."
                className="h-8 text-sm"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Color</Label>
              <Select
                value={grafica.color}
                onValueChange={(value) => onUpdate(grafica.id, { color: value })}
              >
                <SelectTrigger className="h-8 text-sm">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: grafica.color }}
                    />
                    <span>{COLORES_GRAFICA.find((c) => c.value === grafica.color)?.label || 'Color'}</span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  {COLORES_GRAFICA.map((color) => (
                    <SelectItem key={color.value} value={color.value}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: color.value }}
                        />
                        <span>{color.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Límite de categorías</Label>
              <Input
                type="number"
                value={grafica.limite_categorias}
                onChange={(e) =>
                  onUpdate(grafica.id, { limite_categorias: Math.min(50, Math.max(1, parseInt(e.target.value) || 10)) })
                }
                min={1}
                max={50}
                className="h-8 text-sm"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id={`mostrar_etiquetas_linea_${grafica.id}`}
              checked={grafica.mostrar_etiquetas}
              onCheckedChange={(checked) =>
                onUpdate(grafica.id, { mostrar_etiquetas: checked as boolean })
              }
            />
            <Label htmlFor={`mostrar_etiquetas_linea_${grafica.id}`} className="text-xs cursor-pointer">
              Mostrar valores en los puntos
            </Label>
          </div>
        </div>

        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive hover:text-destructive shrink-0"
          onClick={() => onRemove(grafica.id)}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}

// Graficas Lineas Configuration Component
interface GraficasLineasConfigurationProps {
  config: GraficasLineasConfig
  columnas: ColumnaTabla[]
  onChange: (config: GraficasLineasConfig) => void
}

function GraficasLineasConfiguration({ config, columnas, onChange }: GraficasLineasConfigurationProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const graficas = config?.graficas ?? []

  const handleToggle = (habilitado: boolean) => {
    onChange({ ...config, habilitado, graficas })
  }

  const handleAddGrafica = () => {
    const newGrafica: GraficaLineasItemConfig = {
      id: generateId(),
      columna_agrupacion: columnas[0]?.nombre || '',
      titulo: `Tendencia ${graficas.length + 1}`,
      color: COLORES_GRAFICA[graficas.length % COLORES_GRAFICA.length].value,
      mostrar_etiquetas: true,
      limite_categorias: 10,
      orden: graficas.length,
    }
    onChange({
      ...config,
      graficas: [...graficas, newGrafica],
    })
  }

  const handleUpdateGrafica = (id: string, updates: Partial<GraficaLineasItemConfig>) => {
    onChange({
      ...config,
      graficas: graficas.map((g) =>
        g.id === id ? { ...g, ...updates } : g
      ),
    })
  }

  const handleRemoveGrafica = (id: string) => {
    onChange({
      ...config,
      graficas: graficas.filter((g) => g.id !== id),
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = graficas.findIndex((g) => g.id === active.id)
      const newIndex = graficas.findIndex((g) => g.id === over.id)
      const newGraficas = arrayMove(graficas, oldIndex, newIndex).map((g, i) => ({
        ...g,
        orden: i,
      }))
      onChange({ ...config, graficas: newGraficas })
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-semibold text-sm">Gráficas de Líneas</h3>
        </div>
        <Switch checked={config?.habilitado ?? false} onCheckedChange={handleToggle} />
      </div>

      {config?.habilitado && (
        <div className="space-y-3 pl-6">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={graficas.map((g) => g.id)}
              strategy={verticalListSortingStrategy}
            >
              {graficas.map((grafica, index) => (
                <SortableGraficaLineaItem
                  key={grafica.id}
                  grafica={grafica}
                  columnas={columnas}
                  onUpdate={handleUpdateGrafica}
                  onRemove={handleRemoveGrafica}
                  index={index}
                />
              ))}
            </SortableContext>
          </DndContext>

          {graficas.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No hay gráficas configuradas. Agrega una para visualizar tendencias.
            </p>
          )}

          <Button
            type="button"
            variant="outline"
            size="sm"
            className="w-full"
            onClick={handleAddGrafica}
          >
            <Plus className="h-4 w-4 mr-2" />
            Agregar Gráfica de Líneas
          </Button>
        </div>
      )}
    </div>
  )
}

// Main VisualizacionStep Component
interface VisualizacionStepProps {
  config: VisualizacionConfig | null | undefined
  columnas: ColumnaTabla[]
  onChange: (config: VisualizacionConfig) => void
}

// Helper para migrar configuración antigua a nueva
function migrateConfig(rawConfig: VisualizacionConfig | null | undefined): VisualizacionConfig {
  // Si tiene formato antiguo (grafica_barras como objeto) y no tiene formato nuevo
  if (rawConfig?.grafica_barras && !rawConfig?.graficas_verticales) {
    const oldConfig = rawConfig.grafica_barras
    return {
      cards: rawConfig.cards || { habilitado: false, cards: [] },
      graficas_verticales: {
        habilitado: oldConfig.habilitado,
        graficas: oldConfig.habilitado && oldConfig.columna_agrupacion ? [{
          id: 'migrated-1',
          columna_agrupacion: oldConfig.columna_agrupacion,
          titulo: oldConfig.titulo || 'Distribución',
          color: oldConfig.color || '#2fbc80',
          mostrar_etiquetas: oldConfig.mostrar_etiquetas ?? true,
          limite_categorias: oldConfig.limite_categorias ?? 10,
          orden: 0,
        }] : [],
      },
      graficas_horizontales: {
        habilitado: false,
        graficas: [],
      },
      graficas_lineas: {
        habilitado: false,
        graficas: [],
      },
    }
  }

  // Retornar config con defaults
  return {
    cards: rawConfig?.cards || { habilitado: false, cards: [] },
    graficas_verticales: rawConfig?.graficas_verticales || { habilitado: false, graficas: [] },
    graficas_horizontales: rawConfig?.graficas_horizontales || { habilitado: false, graficas: [] },
    graficas_lineas: rawConfig?.graficas_lineas || { habilitado: false, graficas: [] },
  }
}

export function VisualizacionStep({ config: rawConfig, columnas, onChange }: VisualizacionStepProps) {
  // Migrar y normalizar la configuración
  const config = migrateConfig(rawConfig)

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-sm mb-1">Configurar Visualización</h3>
        <p className="text-xs text-muted-foreground">
          Define las métricas y gráficas que se mostrarán en el reporte. Las gráficas se distribuyen 2 por fila.
        </p>
      </div>

      {/* Cards Configuration */}
      <div className="rounded-lg border p-4">
        <CardsConfiguration
          config={config.cards}
          columnas={columnas}
          onChange={(cards) => onChange({ ...config, cards })}
        />
      </div>

      <Separator />

      {/* Graficas Verticales Configuration */}
      <div className="rounded-lg border p-4">
        <GraficasBarrasConfiguration
          config={config.graficas_verticales!}
          columnas={columnas}
          onChange={(graficas_verticales) => onChange({ ...config, graficas_verticales })}
          tipo="vertical"
        />
      </div>

      <Separator />

      {/* Graficas Horizontales Configuration */}
      <div className="rounded-lg border p-4">
        <GraficasBarrasConfiguration
          config={config.graficas_horizontales!}
          columnas={columnas}
          onChange={(graficas_horizontales) => onChange({ ...config, graficas_horizontales })}
          tipo="horizontal"
        />
      </div>

      <Separator />

      {/* Graficas de Líneas Configuration */}
      <div className="rounded-lg border p-4">
        <GraficasLineasConfiguration
          config={config.graficas_lineas!}
          columnas={columnas}
          onChange={(graficas_lineas) => onChange({ ...config, graficas_lineas })}
        />
      </div>

      {/* Info note */}
      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <span>
          La gráfica de fechas y la tabla de registros siempre estarán presentes en el reporte.
          Los filtros configurados en el paso anterior también estarán disponibles.
        </span>
      </div>
    </div>
  )
}
